import {
    computed,
    Injectable,
    Injector,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatiqUserStateService } from '@dat/datiq-user';
import { endsWith } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
    getDisableVariationKey,
    IDatiqFeatureFlag,
    IDatiqFeatureFlagDisabledVariation,
    IDatiqFeatureFlagStateService,
} from '../models';
import { DatiqFeatureFlagStoreService } from '../store';
import { IqFeatureFlagsDbService } from './datiq-feature-flag-db.service';

@Injectable({
    providedIn: 'root',
})
export class DatiqFeatureFlagStateService
    implements IDatiqFeatureFlagStateService
{
    constructor(
        private _store: DatiqFeatureFlagStoreService,
        private _userState: DatiqUserStateService,
        private _db: IqFeatureFlagsDbService,
        private injector: Injector,
    ) {}

    /**
     * @deprecated Use isLoaded instead
     * @returns
     */
    isLoaded$(): Observable<boolean> {
        return this._store.isLoaded$();
    }

    isLoaded(): Signal<boolean> {
        return this._store.isLoadedSig();
    }

    /**
     *
     * @param featureFlag
     * @param variation
     * @deprecated Use hasVariation instead
     * @returns
     */
    hasVariation$(featureFlag: string, variation: string): Observable<boolean> {
        const isEnabled$: Observable<boolean> = this.areEnabled$([featureFlag]);
        const hasVariation$: Observable<boolean> = this._store.hasVariation$(
            featureFlag,
            variation,
        );

        return combineLatest([hasVariation$, isEnabled$]).pipe(
            map(
                ([hasVariation, isEnabled]: [boolean, boolean]) =>
                    isEnabled && hasVariation,
            ),
        );
    }

    hasVariation(featureFlag: string, variation: string): Signal<boolean> {
        const isEnabled: Signal<boolean> = this.areEnabled([featureFlag]);
        const hasVariation: Signal<boolean> = this._store.hasVariationSig(
            featureFlag,
            variation,
        );

        return computed(() => {
            return isEnabled() && hasVariation();
        });
    }

    /**
     *
     * @deprecated Use getEnabledFlag instead
     * @param featureFlag
     */
    getEnabledFlag$(featureFlag: string): Observable<IDatiqFeatureFlag> {
        return this._store.getEnabledFlag$(featureFlag);
    }

    getEnabled(featureFlag: string): Signal<IDatiqFeatureFlag> {
        return this._store.getEnabledFlagSig(featureFlag);
    }

    getDisabledVariables(
        featureFlagKeys: string[],
        flagSig?: Signal<string[]>,
    ): Signal<IDatiqFeatureFlagDisabledVariation[]> {
        const disabledVariations: WritableSignal<
            IDatiqFeatureFlagDisabledVariation[]
        > = signal([]);

        return computed(() => {
            const flagKeys: string[] = flagSig?.() ?? featureFlagKeys ?? [];

            if (!flagKeys.length) return [];

            const variations: IDatiqFeatureFlagDisabledVariation[] = this._store
                .getEnabledFlagListSig(flagKeys)()
                .filter(
                    (flag: IDatiqFeatureFlag): boolean =>
                        flag.variation ===
                        getDisableVariationKey(flag.variation),
                )
                .map(
                    (flag: IDatiqFeatureFlag) =>
                        flag.variables as IDatiqFeatureFlagDisabledVariation,
                );

            return variations;
        });
    }

    /**
     *
     * @deprecated Use getEnabledFlags instead
     * @param featureFlag
     */
    getEnabledFlags$(featureFlags: string[]): Observable<IDatiqFeatureFlag[]> {
        return this._store.getEnabledFlags$(featureFlags);
    }

    getEnabledFlags(featureFlags: string[]): Signal<IDatiqFeatureFlag[]> {
        return toSignal(this._store.getEnabledFlags$(featureFlags), {
            injector: this.injector,
        });
    }

    /**
     * Sees if all provided feature flags are enabled
     *
     * @deprecated Use areEnabled instead
     * @param featureFlags - feature flags to test
     */
    areEnabled$(featureFlagKeys: string[]): Observable<boolean> {
        const areEnabled$: Observable<boolean> =
            this._store.areEnabled$(featureFlagKeys);
        const loggedIn$: Observable<boolean> =
            this._userState.isLoggedInUserLoggedIn$();
        const useFlags$: Observable<boolean> = this._store.useFlags$();
        const isLoaded$: Observable<boolean> = this._store.isLoaded$();

        return combineLatest([
            areEnabled$,
            useFlags$,
            isLoaded$,
            loggedIn$,
        ]).pipe(
            filter(
                ([, , isLoaded, loggedIn]: [
                    boolean,
                    boolean,
                    boolean,
                    boolean,
                ]) => isLoaded && loggedIn,
            ),
            map(
                ([areEnabled, useFlags]: [
                    boolean,
                    boolean,
                    boolean,
                    boolean,
                ]) => {
                    return useFlags ? areEnabled : true;
                },
            ),
        );
    }

    areEnabled(featureFlagKeys: string[]): Signal<boolean> {
        const areEnabled: Signal<boolean> =
            this._store.areEnabledSig(featureFlagKeys);

        const loggedIn: Signal<boolean> =
            this._userState.isLoggedInUserLoggedIn();
        const useFlags: Signal<boolean> = this._store.useFlagsSig();
        const isLoaded: Signal<boolean> = this._store.isLoadedSig();

        return computed(() => {
            if (!isLoaded() || !loggedIn()) return false;

            return useFlags() ? areEnabled() : true;
        });
    }

    /**
     * Returns all feature flags that are enabled
     *
     * @deprecated Use getEnabledFlags instead
     */
    getAllEnabledFeatureFlags$(): Observable<IDatiqFeatureFlag[]> {
        return this._store.getAllEnabledFeatureFlags$();
    }

    getAllEnabledFeatureFlags(): Signal<IDatiqFeatureFlag[]> {
        return toSignal(this._store.getAllEnabledFeatureFlags$(), {
            injector: this.injector,
        });
    }

    /**
     * Returns an object of variables of the feature flags sent. This
     * will only add the variables that are enabled, but will set it to
     * null if none are avaiable
     * @deprecated use getEnabledVariables instead
     * @param featureFlags - Feature flags to get variables from
     */
    getEnabledVariables$(featureFlagKeys: string[]): Observable<unknown> {
        return this._store.getEnabledVariables$(featureFlagKeys);
    }

    getEnabledVariables(featureFlagKeys: string[]): Signal<unknown> {
        return this._store.getEnabledVariablesSig(featureFlagKeys);
    }

    // These will be implemented with this ticket: https://transcore.jira.com/browse/EYEQ-4811

    setDisableVariation(featureFlag: string): void {
        throw new Error('Method not implemented.');
    }

    enableFlag(key: string) {
        throw new Error('Method not implemented.');
    }
    fetchFlag(key: string) {
        throw new Error('Method not implemented.');
    }

    isFlagAnExperiment(flagName: string): boolean {
        const flag = this._store.getFlagsSig([flagName])();
        if (!(flag && flag.length > 0)) return false;
        return endsWith(flag[0]?.ruleKey, 'experiment');
    }

    getFlags(flagNames: string[]): Signal<IDatiqFeatureFlag[]> {
        return this._store.getFlagsSig(flagNames);
    }

    isExperimentFlagEnrolled(flagName: string): Observable<boolean> {
        const flag = this._store.getFlagsSig([flagName])()[0];
        if (!flag) return of(false);
        return this._db.hasFlag(flag.key).pipe(
            switchMap((isFlagStored: boolean) => {
                if (!isFlagStored) return of(false);
                return this._db
                    .getFlag(flag.key)
                    .pipe(
                        map(
                            (storedFlag) =>
                                storedFlag?.toJSON()?.variationKey ===
                                flag.variation,
                        ),
                    );
            }),
        );
    }

    enrollExperimentFlag(flagName: string): Observable<void> {
        this._store.dispatchEnrollExperimentFlag(flagName);
        const flags = this._store.getFlagsSig([flagName])();
        if (flags.length === 0) return of();
        const flag = flags[0];
        const isExperiment = endsWith(flag.ruleKey, 'experiment');
        if (!isExperiment) {
            return this._db.removeFlag(flagName);
        }
        const { ruleKey, variation } = flag;
        return this._db.upsertFlag(flagName, ruleKey, variation);
    }
}
